import React, {
  useCallback, useRef,
} from 'react';
import { connect } from 'react-redux';
import {
  Dropdown,
} from 'semantic-ui-react';
import {
  Other,
} from 'simplydo/interfaces';
import styled from 'styled-components';
import api from 'api';
import util from 'utils/utils';
import toast from 'react-hot-toast';
import { ThemedButton } from 'components/lib/UI';
import { useContainerDimensions } from 'utils/useContainerDimensions';
import Impact from './Impact';

const IdeaImpactHeader = styled.div`
  > span {
    display: block;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }
  .impact-name {}
  .impact-description {
    opacity: 0.7;
  }
`;

const IdeaImpactDropdownItem = styled(Dropdown.Item)`
  display: flex !important;
  overflow: hidden;
  text-overflow: ellipsis;
  > i {
    width: 10px;
  }
  ${IdeaImpactHeader} {
    max-width: 95%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: normal;
  }
`;

type ImpactProps = {
  idea: Other.IIdea;
  onUpdateIdea: (idea: Partial<Other.IIdea>) => void;
  impactList: Other.IImpact[];
}

function Impacts({
  idea, onUpdateIdea, impactList,
} : ImpactProps) {
  const ideaId = idea._id;
  const dropdownRef = useRef(null);
  const { width } = useContainerDimensions(dropdownRef);

  const addImpact = useCallback((impactId) => {
    let authToken = null;
    if (util.localStorageIsSupported()) {
      authToken = localStorage.getItem(`ideaAuthToken:${ideaId}`);
    }
    api.ideas.addImpactMeasure(ideaId, impactId, { authToken }, (data) => {
      onUpdateIdea({ impacts: [...(idea.impacts ?? []), data.newImpact] });
    }, (err) => {
      toast.error(err.message);
    });
  }, [ideaId, onUpdateIdea, idea.impacts]);

  const deleteImpact = useCallback((impactId, impactMeasureId) => {
    util.confirm(
      'Are you sure you want to delete this impact measure?',
      'This action cannot be undone.',
    ).then(() => {
      let authToken = null;
      if (util.localStorageIsSupported()) {
        authToken = localStorage.getItem(`ideaAuthToken:${ideaId}`);
      }
      const usingImpact = idea.impacts.find((i) => i.idea === impactId);
      api.ideas.removeImpactMeasure(usingImpact.idea, impactId, impactMeasureId, { authToken }, () => {
        onUpdateIdea({ impacts: idea.impacts.filter((i) => i._id !== impactMeasureId) });
      }, (err) => {
        toast.error(err.message);
      });
    }).catch(() => {});
  }, [idea.impacts, ideaId, onUpdateIdea]);

  const onUpdateIdeaImpact = useCallback((impactId, data) => {
    onUpdateIdea({
      impacts: idea.impacts.map((impact) => {
        if (impact._id === impactId) {
          return { ...impact, ...data };
        }
        return impact;
      }),
    });
  }, [idea.impacts, onUpdateIdea]);

  const onUpdateIdeaTotalImpacts = useCallback((totalImpacts) => {
    onUpdateIdea({
      totalImpacts,
    });
  }, [onUpdateIdea]);

  if (!(impactList?.length > 0)) return null;
  return (
    <div
      ref={dropdownRef}
    >
      <p>How will this idea impact "{idea.ownerChallenge?.name}"?</p>

      {idea.impacts?.map((i) => (
        <Impact
          key={i._id}
          idea={idea}
          impact={i}
          onUpdateIdeaImpact={onUpdateIdeaImpact}
          deleteImpact={deleteImpact}
          onUpdateIdeaTotalImpacts={onUpdateIdeaTotalImpacts}
        />
      ))}

      <Dropdown
        icon={null}
        pointing={idea.impacts?.length > 0 ? 'bottom' : 'top'}
        fluid
        trigger={
          <ThemedButton size='small' primary icon='plus' content='Add new impact measure' fluid />
        }
      >
        <Dropdown.Menu style={{ left: 'auto', right: 'auto', width }}>
          {impactList.map((i, idx) => (
            <IdeaImpactDropdownItem
              key={idx}
              icon={i.icon}
              content={(
                <IdeaImpactHeader>
                  <span className='impact-name'>{i.name}</span>
                  <span className='impact-description'>{i.description}</span>
                </IdeaImpactHeader>
              )}
              // description={i.description}
              onClick={() => addImpact(i._id)}
              style={{ width }}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(Impacts);
